"use client";

import { useMemo, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, A11y } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useT } from "@/lib/i18n/context";
import type { WClientTestimonialsCardsContent } from "@/lib/api/cms-window-content";
import { wClientTestimonialsCardsTranslations } from "./WClientTestimonialsCards.i18n";
import styles from "./WClientTestimonialsCards.module.css";

/**
 * WClientTestimonialsCards - okno "Opinie klientow (karty)".
 *
 * Wariant `WClientTestimonials` - identyczne dane (losowe testimonials z c_dedicated_realizations),
 * inna prezentacja: grid 3 kart na desktopie (Swiper multi-slide) zamiast 1 slide na raz.
 *
 * Struktura:
 *   - Eyebrow (hero_text) - uppercase label akcent nad tytulem
 *   - H2 (title lub defaultTitle) - "Co mowia o nas klienci"
 *   - Lead (lead_text) - paragraf pod tytulem
 *   - Strzalki nav w prawym gornym rogu (nad slajdami, nie pod)
 *   - Swiper: 3 slides na desktopie, 2 na tablecie, 1 na mobile
 *   - Karta: zielona ikona cytatu + testimonial HTML + separator + nazwa + sektor
 *   - Pagination dots pod slajdami
 */
export function WClientTestimonialsCards({ content, title }: WClientTestimonialsCardsProps) {
  const t = useT(wClientTestimonialsCardsTranslations);
  const swiperRef = useRef<SwiperInstance | null>(null);

  const items = useMemo(() => content?.items ?? [], [content]);

  if (!content || items.length === 0) return null;

  const heading = title || t.defaultTitle;
  const eyebrow = (content.hero_text ?? "").trim();
  const lead = (content.lead_text ?? "").trim();
  const enableNav = items.length > 3;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
          {lead !== "" ? <p className={styles.lead}>{lead}</p> : null}
        </div>
        {enableNav ? (
          <div className={styles.controls}>
            <button
              type="button"
              className={styles.prevBtn}
              aria-label={t.prev}
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.nextBtn}
              aria-label={t.next}
              onClick={() => swiperRef.current?.slideNext()}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>

      <Swiper
        modules={[Pagination, Navigation, A11y]}
        onSwiper={(sw) => { swiperRef.current = sw; }}
        slidesPerView={1}
        spaceBetween={20}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 20 },
          960: { slidesPerView: 3, spaceBetween: 24 },
        }}
        pagination={enableNav ? { clickable: true, el: `.${styles.pagination}` } : false}
        a11y={{ enabled: true, prevSlideMessage: t.prev, nextSlideMessage: t.next, paginationBulletMessage: t.goToSlide }}
        className={styles.swiper}
      >
        {items.map((item) => (
          <SwiperSlide key={item.id} className={styles.slide}>
            <article className={styles.card}>
              <span className={styles.quoteIcon} aria-hidden="true">&#8220;</span>
              <div
                className={styles.testimonial}
                dangerouslySetInnerHTML={{ __html: item.testimonial_html }}
              />
              <div className={styles.separator} aria-hidden="true" />
              <div className={styles.author}>
                <span className={styles.authorName}>{item.client_name}</span>
                {item.client_sector ? (
                  <span className={styles.authorSector}>{item.client_sector}</span>
                ) : null}
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>

      {enableNav ? <div className={styles.pagination} /> : null}
    </section>
  );
}

export type WClientTestimonialsCardsProps = {
  content: WClientTestimonialsCardsContent | null;
  title: string;
};

export default WClientTestimonialsCards;
